import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (phoneNumber: string, password: string) => Promise<void>;
  register: (data: {
    phoneNumber: string;
    password: string;
    firstName: string;
    lastName?: string;
  }) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isAgent: () => boolean;
  isShopOwner: () => boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (phoneNumber: string, password: string) => {
    const response: AuthResponse = await authAPI.login(phoneNumber, password);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));
  };

  const register = async (data: {
    phoneNumber: string;
    password: string;
    firstName: string;
    lastName?: string;
  }) => {
    const response: AuthResponse = await authAPI.register(data);
    setToken(response.access_token);
    setUser(response.user);
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const isAdmin = () => {
    if (!user || !user.roles) return false;
    // Assuming ADMIN role has ID 2 (based on previous context)
    return user.roles.some((role) => role.name === 'admin');
  };

  const isAgent = () => {
    if (!user || !user.roles) return false;
    return user.roles.some((role) => role.name === 'agent');
  };

  const isShopOwner = () => {
    if (!user || !user.roles) return false;
    return user.roles.some((role) => role.name === 'shop_owner');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, isAdmin, isAgent, isShopOwner, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
